import {
  BadgePoundSterling,
  BarChart3,
  BellRing,
  Building2,
  CalendarCheck,
  CalendarRange,
  ClipboardList,
  Contact,
  FileInput,
  Globe,
  Headset,
  Home,
  Languages,
  ListChecks,
  MapPin,
  MessageSquareText,
  Package,
  Receipt,
  ScrollText,
  Settings,
  ShieldCheck,
  Truck,
  UserCog,
  Users,
  Warehouse,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { Permission } from "@/lib/permissions";

export interface NavItem {
  /** i18n key for the item label. */
  label: string;
  href: string;
  icon: LucideIcon;
  /** The item is shown when the principal holds ANY of these permissions. */
  permissions: string[];
  /** Optional i18n key for a short hint shown in the collapsed sidebar tooltip. */
  hint?: string;
  /** When set, the nav button shows a live count badge for this feed. */
  badgeKey?: string;
}

export interface NavSection {
  /** i18n key for the section heading. */
  title: string;
  items: NavItem[];
}

export const NAV_SECTIONS: NavSection[] = [
  {
    title: "nav.general",
    items: [
      {
        label: "nav.summary",
        href: "/admin",
        icon: Home,
        permissions: [Permission.USERS_READ, Permission.LOADS_READ, Permission.SLOTS_APPROVE],
        hint: "nav.summaryHint",
      },
      {
        label: "nav.loads",
        href: "/admin/loads",
        icon: Package,
        permissions: [Permission.LOADS_READ],
        hint: "nav.loadsHint",
      },
      {
        label: "nav.collections",
        href: "/admin/collections",
        icon: Package,
        permissions: [Permission.LOADS_READ],
        hint: "nav.collectionsHint",
      },
      {
        label: "nav.routePlanner",
        href: "/admin/route-planner",
        icon: MapPin,
        permissions: [Permission.LOADS_READ],
        hint: "nav.routePlannerHint",
      },
      {
        label: "nav.loadScheduler",
        href: "/admin/scheduler",
        icon: CalendarRange,
        permissions: [Permission.SLOTS_READ],
        hint: "nav.loadSchedulerHint",
      },
      {
        label: "nav.containerLoadPlanner",
        href: "/admin/container-load-planner",
        icon: Package,
        permissions: [Permission.LOADPLANS_READ],
        hint: "nav.containerLoadPlannerHint",
      },
      {
        label: "nav.slotApprovals",
        href: "/admin/slot-approvals",
        icon: CalendarCheck,
        permissions: [Permission.SLOTS_APPROVE],
        hint: "nav.slotApprovalsHint",
        badgeKey: "slot-approvals",
      },
      {
        label: "nav.notifications",
        href: "/admin/notifications",
        icon: BellRing,
        permissions: [Permission.NOTIFICATIONS_READ],
        hint: "nav.notificationsHint",
      },
    ]
  },
  {
    title: "nav.portals",
    items: [
      {
        label: "nav.loadboard",
        href: "/loadboard",
        icon: ClipboardList,
        permissions: [Permission.LOADBOARD_READ, Permission.LOADS_READ],
        hint: "nav.loadboardHint",
      },
      {
        label: "nav.depot",
        href: "/depot",
        icon: Warehouse,
        permissions: [Permission.DEPOT_READ, Permission.LOADS_READ],
        hint: "nav.depotHint",
      },
      {
        label: "nav.haulier",
        href: "/haulier",
        icon: Truck,
        permissions: [Permission.HAULIER_LOADS_READ, Permission.LOADS_READ],
        hint: "nav.haulierHint",
      },
    ],
  },
  {
    title: "nav.finance",
    items: [
      {
        label: "nav.invoices",
        href: "/admin/invoices",
        icon: Receipt,
        permissions: [Permission.INVOICES_READ],
        hint: "nav.invoicesHint",
      },
      {
        label: "nav.rateCards",
        href: "/admin/rate-cards",
        icon: BadgePoundSterling,
        permissions: [Permission.RATE_CARDS_READ],
        hint: "nav.rateCardsHint",
      },
    ],
  },
  {
    title: "nav.settings",
    items: [
      {
        label: "nav.customers",
        href: "/admin/customers",
        icon: Building2,
        permissions: [Permission.CUSTOMERS_READ],
        hint: "nav.customersHint",
      },
      {
        label: "nav.hauliers",
        href: "/admin/hauliers",
        icon: Truck,
        permissions: [Permission.HAULIERS_READ],
        hint: "nav.hauliersHint",
      },
      {
        label: "nav.cse",
        href: "/admin/cse",
        icon: Headset,
        permissions: [Permission.CSE_READ],
        hint: "nav.cseHint",
      },
      {
        label: "nav.users",
        href: "/admin/users",
        icon: Users,
        permissions: [Permission.USERS_READ],
        hint: "nav.usersHint",
      },
      {
        label: "nav.contacts",
        href: "/admin/contacts",
        icon: Contact,
        permissions: [Permission.CONTACTS_READ],
        hint: "nav.contactsHint",
      },
      {
        label: "nav.groups",
        href: "/admin/groups",
        icon: UserCog,
        permissions: [Permission.GROUPS_READ],
        hint: "nav.groupsHint",
      },
      {
        label: "nav.permissions",
        href: "/admin/permissions",
        icon: ShieldCheck,
        permissions: [Permission.PERMISSIONS_READ],
        hint: "nav.permissionsHint",
      },
      {
        label: "nav.sites",
        href: "/admin/sites",
        icon: Warehouse,
        permissions: [Permission.SITES_READ],
        hint: "nav.sitesHint",
      },
      {
        label: "nav.bays",
        href: "/admin/bays",
        icon: MapPin,
        permissions: [Permission.BAYS_READ],
        hint: "nav.baysHint",
      },
      {
        label: "nav.vehicleTypes",
        href: "/admin/vehicle-types",
        icon: Truck,
        permissions: [Permission.LOADPLANS_READ],
        hint: "nav.vehicleTypesHint",
      },
      {
        label: "nav.audit",
        href: "/admin/audit",
        icon: ScrollText,
        permissions: [Permission.AUDIT_READ],
        hint: "nav.auditHint",
      },
      {
        label: "nav.reports",
        href: "/admin/reports",
        icon: BarChart3,
        permissions: [Permission.REPORTS_READ],
        hint: "nav.reportsHint",
      },
      {
        label: "nav.imports",
        href: "/admin/imports",
        icon: FileInput,
        permissions: [Permission.IMPORTS_READ],
        hint: "nav.importsHint",
      },
      {
        label: "nav.notificationTemplates",
        href: "/admin/notification-templates",
        icon: MessageSquareText,
        permissions: [Permission.NOTIFICATIONS_WRITE],
        hint: "nav.notificationTemplatesHint",
      },
      {
        label: "nav.notificationRules",
        href: "/admin/notification-rules",
        icon: ListChecks,
        permissions: [Permission.NOTIFICATIONS_WRITE],
        hint: "nav.notificationRulesHint",
      },
      {
        label: "nav.languages",
        href: "/admin/languages",
        icon: Languages,
        permissions: [Permission.LANGUAGES_READ],
        hint: "nav.languagesHint",
      },
      {
        label: "nav.translations",
        href: "/admin/translations",
        icon: Globe,
        permissions: [Permission.TRANSLATIONS_READ],
        hint: "nav.translationsHint",
      },
      {
        label: "nav.settings",
        href: "/admin/settings",
        icon: Settings,
        permissions: [Permission.SETTINGS_READ],
        hint: "nav.settingsHint",
      },
    ],
  },
];
